import { soundAction, soundChain, WORLD_H } from '@tanky/shared';
import { SoundLibrary } from './library';
import { azimuth2D, azimuth3D, CH, distanceCutoff, distanceGain, surroundGains } from './pan';

export type OutputMode = 'auto' | 'surround' | 'stereo';

export interface PlayOpts {
  /** Poloha ve světě (x, y jako ve 2D – y dolů, z = hloubka ve 3D). */
  x?: number;
  y?: number;
  z?: number;
  volume?: number;
  /** Smyčku zastavit po tolika sekundách. */
  duration?: number;
  /** Rychlost přehrávání (výška); výchozí = drobná náhodná variace. */
  rate?: number;
}

export interface Voice {
  setPosition(x: number, y?: number, z?: number): void;
  setVolume(v: number): void;
  stop(fade?: number): void;
}

/** Posluchač ve 3D (souřadnice Three.js: y nahoru) a směr pohledu v rovině xz. */
export interface Listener3D {
  x: number;
  y: number;
  z: number;
  fx: number;
  fz: number;
}

const MAX_VOICES = 24;
const LFE_CUTOFF = 120;

/**
 * Přehrávání zvuků z knihovny do 6kanálové sběrnice (5.1: L, R, C, LFE, Ls, Rs).
 * Když výstupní zařízení 5.1 neumí, sběrnici podle pravidel Web Audio smíchá
 * prohlížeč do sterea sám.
 */
export class AudioEngine {
  readonly library = new SoundLibrary();
  private ctx: AudioContext | null = null;
  private bus: GainNode | null = null;
  private master: GainNode | null = null;
  private voices: VoiceImpl[] = [];
  private listener2D = { x: 800, y: 450, halfSpan: 800 };
  private listener3D: Listener3D | null = null;
  private ear: { x: number; y: number } | null = null;
  output: OutputMode = 'auto';
  volume = 0.8;

  constructor() {
    this.library.setContext(() => this.audio());
  }

  /** Kontext vzniká líně (prohlížeče ho pustí až po interakci uživatele). */
  audio(): AudioContext | null {
    if (this.ctx) return this.ctx;
    const Ctor = window.AudioContext ?? (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!Ctor) return null;
    const ctx = new Ctor({ sampleRate: 48000 });
    this.ctx = ctx;
    this.bus = ctx.createGain();
    this.bus.channelCount = 6;
    this.bus.channelCountMode = 'explicit';
    this.bus.channelInterpretation = 'speakers';
    this.master = ctx.createGain();
    this.master.channelCount = 6;
    this.master.channelCountMode = 'explicit';
    this.master.channelInterpretation = 'speakers';
    this.master.gain.value = this.volume;
    this.bus.connect(this.master).connect(ctx.destination);
    this.applyOutput();
    this.library.preload();
    return ctx;
  }

  get running(): boolean {
    return this.ctx?.state === 'running';
  }

  resume() {
    const ctx = this.audio();
    if (ctx && ctx.state === 'suspended') ctx.resume().catch(() => {});
  }

  suspend() {
    this.ctx?.suspend().catch(() => {});
  }

  /** Umí zařízení 5.1? */
  get surroundCapable(): boolean {
    return (this.ctx?.destination.maxChannelCount ?? 2) >= 6;
  }

  /** Hraje se teď do 6 kanálů? */
  get surroundActive(): boolean {
    return (this.ctx?.destination.channelCount ?? 2) >= 6;
  }

  setOutput(mode: OutputMode) {
    this.output = mode;
    this.applyOutput();
  }

  private applyOutput() {
    const ctx = this.ctx;
    if (!ctx) return;
    const d = ctx.destination;
    const want = this.output === 'stereo' ? 2 : d.maxChannelCount >= 6 ? 6 : 2;
    try {
      d.channelCountMode = 'explicit';
      d.channelInterpretation = 'speakers';
      d.channelCount = Math.min(want, d.maxChannelCount);
    } catch {
      /* zařízení nastavení odmítlo – zůstane výchozí */
    }
  }

  setVolume(v: number) {
    this.volume = Math.max(0, Math.min(1, v));
    if (this.master && this.ctx) this.master.gain.setTargetAtTime(this.volume, this.ctx.currentTime, 0.05);
  }

  setListener2D(x: number, y: number, halfSpan: number) {
    this.listener2D = { x, y, halfSpan: Math.max(100, halfSpan) };
    this.listener3D = null;
    this.update();
  }

  setListener3D(l: Listener3D) {
    this.listener3D = l;
    this.update();
  }

  /** Přepočítá rozmístění zvuků, které mají polohu (posluchač se pohnul). */
  update() {
    for (const v of this.voices) v.reposition();
  }

  /** Kde hráč „slyší“ (jeho tank, světové 2D souřadnice); null = střed záběru kamery. */
  setEar(p: { x: number; y: number } | null) {
    const e = this.ear;
    if (p === e || (p && e && p.x === e.x && p.y === e.y)) return;
    this.ear = p ? { x: p.x, y: p.y } : null;
    this.update();
  }

  /**
   * Směr, útlum a tlumení výšek zdroje v poloze (x, y, z). Směr se bere od kamery
   * (odpovídá obrazu), hlasitost od mého tanku – výbuch u mě je nahlas, na druhém konci mapy potichu.
   */
  place(x: number, y = 0, z = 0): { az: number; att: number; spread: number; cutoff: number } {
    const l = this.listener3D;
    let az: number;
    let spread = 0;
    let d: number;
    if (l) {
      const sy = WORLD_H - y;
      const camDist = Math.hypot(x - l.x, sy - l.y, z - l.z);
      az = azimuth3D({ x: l.x, z: l.z }, { x: l.fx, z: l.fz }, { x, z });
      spread = camDist < 70 ? 0.5 : 0;
      // Bez tanku od kamery; ta bývá kus od bitvy, proto škála jako dřív (naplno do 260)
      d = camDist * (150 / 260);
    } else {
      const dx = x - this.listener2D.x;
      az = azimuth2D(dx, this.listener2D.halfSpan);
      d = Math.hypot(dx, y - this.listener2D.y);
    }
    if (this.ear) d = Math.hypot(x - this.ear.x, y - this.ear.y, z);
    return { az, att: distanceGain(d), spread, cutoff: distanceCutoff(d) };
  }

  /**
   * Přehraje akci: náhodný z přiřazených zvuků (případně z náhradní akce).
   * Vrací null, když zvuk vypnutý, akce nemá zvuk nebo se ještě stahuje.
   */
  play(actionId: string, opts: PlayOpts = {}): Voice | null {
    const ctx = this.ctx;
    if (!ctx || ctx.state !== 'running' || !this.bus) return null;
    const action = soundAction(actionId) ?? soundAction('fire.default');
    let binding = null;
    for (const id of soundChain(actionId)) {
      const list = this.library.bindings(id);
      if (list.length) {
        binding = list[Math.floor(Math.random() * list.length)];
        break;
      }
    }
    if (!binding || !action) return null;
    const buffer = this.library.buffer(binding.soundId);
    if (!buffer) return null;
    while (this.voices.length >= MAX_VOICES) this.voices.shift()!.stop(0.05);
    const spatial = action.spatial && opts.x !== undefined;
    const v = new VoiceImpl(this, ctx, this.bus, buffer, {
      loop: !!action.loop,
      volume: (opts.volume ?? 1) * binding.volume,
      lfe: action.lfe,
      spatial,
      rate: opts.rate ?? (action.loop ? 1 : 1 + (Math.random() - 0.5) * 0.06),
      pos: spatial ? { x: opts.x!, y: opts.y ?? 0, z: opts.z ?? 0 } : null,
    });
    this.voices.push(v);
    if (opts.duration !== undefined && action.loop) setTimeout(() => v.stop(0.3), opts.duration * 1000);
    return v;
  }

  /** Přehraje konkrétní zvuk z knihovny (náhled na stránce Zvuky). */
  async preview(soundId: string, opts: { az?: number; volume?: number } = {}): Promise<Voice | null> {
    const ctx = this.audio();
    if (!ctx || !this.bus) return null;
    if (ctx.state !== 'running') await ctx.resume().catch(() => {});
    const buffer = await this.library.fetch(soundId);
    if (!buffer) return null;
    const v = new VoiceImpl(this, ctx, this.bus, buffer, {
      loop: false,
      volume: opts.volume ?? 1,
      lfe: 0.3,
      spatial: opts.az !== undefined,
      rate: 1,
      pos: null,
      fixedAz: opts.az,
    });
    this.voices.push(v);
    return v;
  }

  /** Test reproduktorů: krátký šum do jednoho kanálu (0–5). */
  testChannel(ch: number) {
    const ctx = this.audio();
    if (!ctx || !this.bus) return;
    ctx.resume().catch(() => {});
    const len = Math.floor(ctx.sampleRate * 0.7);
    const buf = ctx.createBuffer(1, len, ctx.sampleRate);
    const d = buf.getChannelData(0);
    for (let i = 0; i < len; i++) {
      const env = Math.min(1, i / 800) * Math.min(1, (len - i) / 4000);
      // LFE: hluboký tón, ostatní: šum
      d[i] = env * (ch === CH.LFE ? Math.sin((i / ctx.sampleRate) * Math.PI * 2 * 50) * 0.9 : (Math.random() * 2 - 1) * 0.5);
    }
    const src = ctx.createBufferSource();
    src.buffer = buf;
    const merger = ctx.createChannelMerger(6);
    src.connect(merger, 0, ch);
    merger.connect(this.bus);
    src.onended = () => merger.disconnect();
    src.start();
  }

  forget(v: VoiceImpl) {
    const i = this.voices.indexOf(v);
    if (i >= 0) this.voices.splice(i, 1);
  }
}

class VoiceImpl implements Voice {
  private src: AudioBufferSourceNode;
  private vol: GainNode;
  private gains: GainNode[] = [];
  private lfe: GainNode | null = null;
  private muffle: BiquadFilterNode | null = null;
  private pos: { x: number; y: number; z: number } | null;
  private stopped = false;

  constructor(
    private engine: AudioEngine,
    private ctx: AudioContext,
    bus: AudioNode,
    buffer: AudioBuffer,
    private o: { loop: boolean; volume: number; lfe: number; spatial: boolean; rate: number; pos: { x: number; y: number; z: number } | null; fixedAz?: number },
  ) {
    this.pos = o.pos;
    const src = ctx.createBufferSource();
    src.buffer = buffer;
    src.loop = o.loop;
    src.playbackRate.value = o.rate;
    this.src = src;
    this.vol = ctx.createGain();
    this.vol.gain.value = o.volume;
    src.connect(this.vol);

    if (buffer.numberOfChannels >= 6) {
      // Hotový mix 5.1: rovnou do sběrnice
      this.vol.connect(bus);
    } else if (!o.spatial) {
      // Nesměrový zvuk: mono do středu, stereo do L/R (pravidla Web Audio „speakers“)
      this.vol.connect(bus);
      if (o.lfe > 0) this.lfeSend(bus);
    } else {
      // Prostorově: mono → 5 reproduktorů + subwoofer
      const mono = ctx.createGain();
      mono.channelCount = 1;
      mono.channelCountMode = 'explicit';
      mono.channelInterpretation = 'speakers';
      this.vol.connect(mono);
      // Vzdálené zvuky tlumeněji (vysoké frekvence se na dálku ztrácí)
      this.muffle = ctx.createBiquadFilter();
      this.muffle.type = 'lowpass';
      this.muffle.Q.value = 0.5;
      mono.connect(this.muffle);
      const merger = ctx.createChannelMerger(6);
      for (let ch = 0; ch < 6; ch++) {
        if (ch === CH.LFE) continue;
        const g = ctx.createGain();
        g.gain.value = 0;
        this.muffle.connect(g).connect(merger, 0, ch);
        this.gains[ch] = g;
      }
      if (o.lfe > 0) {
        const lp = ctx.createBiquadFilter();
        lp.type = 'lowpass';
        lp.frequency.value = LFE_CUTOFF;
        this.lfe = ctx.createGain();
        this.lfe.gain.value = o.lfe;
        mono.connect(lp).connect(this.lfe).connect(merger, 0, CH.LFE);
      }
      merger.connect(bus);
      this.reposition(true);
    }
    src.onended = () => {
      this.stopped = true;
      this.engine.forget(this);
      this.vol.disconnect();
    };
    src.start();
  }

  /** Nesměrový zvuk s basy: kopie přes dolní propust jen do LFE. */
  private lfeSend(bus: AudioNode) {
    const lp = this.ctx.createBiquadFilter();
    lp.type = 'lowpass';
    lp.frequency.value = LFE_CUTOFF;
    const g = this.ctx.createGain();
    g.gain.value = this.o.lfe;
    const merger = this.ctx.createChannelMerger(6);
    const mono = this.ctx.createGain();
    mono.channelCount = 1;
    mono.channelCountMode = 'explicit';
    this.vol.connect(mono).connect(lp).connect(g).connect(merger, 0, CH.LFE);
    merger.connect(bus);
  }

  reposition(now = false) {
    if (!this.gains.length || this.stopped) return;
    let az = this.o.fixedAz ?? 0;
    let att = 1;
    let spread = 0;
    let cutoff = 20000;
    if (this.pos) ({ az, att, spread, cutoff } = this.engine.place(this.pos.x, this.pos.y, this.pos.z));
    const g = surroundGains(az, spread);
    const t = this.ctx.currentTime;
    if (this.muffle) {
      if (now) this.muffle.frequency.value = cutoff;
      else this.muffle.frequency.setTargetAtTime(cutoff, t, 0.03);
    }
    for (let ch = 0; ch < 6; ch++) {
      const node = this.gains[ch];
      if (!node) continue;
      if (now) node.gain.value = g[ch] * att;
      else node.gain.setTargetAtTime(g[ch] * att, t, 0.03);
    }
    if (this.lfe) this.lfe.gain.setTargetAtTime(this.o.lfe * att, t, 0.03);
  }

  setPosition(x: number, y = 0, z = 0) {
    this.pos = { x, y, z };
    this.reposition();
  }

  setVolume(v: number) {
    this.vol.gain.setTargetAtTime(v * this.o.volume, this.ctx.currentTime, 0.05);
  }

  stop(fade = 0.15) {
    if (this.stopped) return;
    this.stopped = true;
    const t = this.ctx.currentTime;
    this.vol.gain.cancelScheduledValues(t);
    this.vol.gain.setValueAtTime(this.vol.gain.value, t);
    this.vol.gain.linearRampToValueAtTime(0, t + fade);
    try {
      this.src.stop(t + fade + 0.02);
    } catch {
      /* už zastavený */
    }
  }
}
